import React, {Component} from 'react';
import PropTypes from 'prop-types';
import Loadable from 'react-loadable';
import {withRouter} from 'react-router';

import {initializePaypal} from '@stubhub/payment-utils';
import {publish} from '@stubhub/pubsub-utils';
import CBTRedirectionModal, {CBT_MODAL_MODES} from '@stubhub/react-cbt-redirection-modal';
import PaypalProvider from '@stubhub/react-paypal-provider';
import Spinner from '@stubhub/react-spinner';
import {connect, ssr_wait} from '@stubhub/react-store-provider';

import {getDefaultQuantityBasedOnListing} from '../../helpers/listing-helper';
import {query, withQuery} from '../../modules/react-query';
import {EVENT_PAGE} from '../../tracking/track-enum';

import ExpiredEvent from './components/react-expired-event';
import {withGoogleEvent} from './components/react-google-event';
import HeaderHacks from './components/react-header-hacks';
import TicketListProvider from './containers/react-ticket-list-provider';
import controller from './controller';
import deviceController from './deviceController';
import {LayoutConfig} from './layouts/layoutConfig';
import {backToEventPage} from './utils/byoHelper';

import './index.scss';

const DesktopLayout = Loadable({
  loader: () => import(/* webpackChunkName: "event" */ './layouts/desktop/'),
  loading: Spinner,
});

const MobileLayout = Loadable({
  loader: () => import(/* webpackChunkName: "event" */ './layouts/mobile/'),
  loading: Spinner,
});

/**
 * This method return web uri for given entity
 * @param  {Array} entity{performers/groupings}
 * @return {String} webURI
 */
const getEntityUrl = (entity) => {
  return entity && entity.length && entity[0] && entity[0].webURI;
};

function getHeaderHeightToOffsetApp() {
  // Use elementSelectors list to add elements typically showing in the header outside of this apps control to offset the height
  const elementSelectors = [
    '#root > div > header',
    '.UnifiedLayout__mainContainer .PencilBanner_content', // Is sometimes present controlled by content
  ];

  let offsetHeight = 0; // Starting base offset
  if (__CLIENT__) {
    const elementsForOffsetCalculation = document.querySelectorAll(elementSelectors.join());

    [...elementsForOffsetCalculation].forEach((el) => {
      offsetHeight = offsetHeight + el.clientHeight;
    });
  }

  return `${offsetHeight}px`;
}

@query({
  priceWithFees: 'priceWithFees',
  showBYO: 'byo',
  quantity: 'qty',
  byoQuantity: 'byo_qty',
  listingId: 'ticket_id',
  listingCategories: 'categ',
  excludeCategories: 'excl',
  deliveryType: 'tdt',
  deliveryTypeInv: 'dt', // TODO: once moving all inventory to use the latest we can use the names for this interface
  sort: 'sort',
  sortKey: 'sortKey',
  sliderMin: 'sliderMin',
  sliderMax: 'sliderMax',
  sectionIdList: 'sid',
  zoneIdList: 'zid',
  priceBucket: 'priceBucket',
  mapType: 'mapType',
  multiSectionSelect: 'multiSectionSelect',
})
@connect(deviceController)
@connect(controller)
@withGoogleEvent
class EventRoyal extends Component {
  static contextTypes = {
    globalRegistry: PropTypes.object.isRequired,
    router: PropTypes.object,
    queue: PropTypes.object, // This is needed by `ssr_wait` in order to work
    logger: PropTypes.object,
  };

  constructor(props) {
    super(props);

    this.state = {
      selectedticketRank: null,
    };

    const {setDefaultQueryValues, isPriceIncludeFeesPreselected} = this.props;

    setDefaultQueryValues({
      multiSectionSelect: true,
      priceWithFees: isPriceIncludeFeesPreselected,
    });
  }

  @ssr_wait
  init() {
    const {eventId} = this.props;

    return this.fetchEvent(eventId);
  }

  UNSAFE_componentWillMount() {
    // First condition for SSR since we dont have an event.
    this.init();
  }

  componentDidMount() {
    publish(EVENT_PAGE.LOADED);
    if (document) {
      document.body.classList.add('EventRoyalBody');
    }
    if (__CLIENT__) {
      const {updateDefaultCurrencies, loaded} = this.props;

      updateDefaultCurrencies();
      if (loaded) {
        publish(EVENT_PAGE.EVENT_DETAILS);
      }
    }
    this.checkVenueForDefaultPriceWithFees();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const {eventId} = this.props;
    const {eventId: nextEventId} = nextProps;
    if (eventId && nextEventId && eventId !== nextEventId) {
      this.fetchEvent(nextEventId);
    }
  }

  initPaypal() {
    const {eventCountry, currencyCode} = this.props;
    const {logger} = this.context;
    this.setState({paypalLoading: true});
    initializePaypal(process.env.REACT_APP_API_HOST, currencyCode, eventCountry)
      .then(({error, paypalClientId, btToken} = {}) => {
        // eslint-disable-next-line promise/always-return
        if (!error) {
          this.setState({
            paypalClientId,
            btToken,
            paypalError: false,
            paypalLoading: false,
          });
        } else {
          this.setState({
            paypalError: true,
            paypalLoading: false,
          });
        }
      })
      .catch((e) => {
        logger.error("Can't initialize paypal and braintree", e);
      });
  }

  componentDidUpdate(prevProps) {
    const {loading, loaded, isPaypalPayLaterEnabled, filterChangeDetection, isQueryUpdated} = this.props;
    const {paypalLoading, paypalError, paypalClientId, btToken} = this.state;

    this.checkVenueForDefaultPriceWithFees(prevProps);
    if (filterChangeDetection && isQueryUpdated) {
      if (isQueryUpdated(prevProps, this.props)) {
        filterChangeDetection(prevProps, this.props);
      } else if (!isQueryUpdated(prevProps, this.props) && prevProps.filterChanged) {
        // Reset that filterchanged prop
        filterChangeDetection();
      }
    }
    if (prevProps.loading && !loading && loaded) {
      publish(EVENT_PAGE.EVENT_DETAILS);
    }
    if (isPaypalPayLaterEnabled && !paypalLoading && !paypalError && (!paypalClientId || !btToken)) {
      this.initPaypal();
    }
  }

  onCloseBYO = () => {
    const {history, setListingSelected} = this.props;
    backToEventPage(history);
    setListingSelected && setListingSelected(null);
  };

  handleSelectTicket = (e, listingId, ticketRank, listing) => {
    const {eventAttributes = {}, quantity: quantityFromURL, updateQuery, setListingSelected} = this.props;

    const preferredBYOQuantity = getDefaultQuantityBasedOnListing({
      listing,
      suggestedQuantity: quantityFromURL,
      eventAttributes,
    });

    updateQuery &&
      updateQuery(
        {
          listingId,
          byoQuantity: preferredBYOQuantity,
          showBYO: true,
        },
        true
      );

    setListingSelected && setListingSelected(listingId);

    this.setState({
      selectedticketRank: ticketRank,
    });
  };

  getselectedticketRank = () => {
    const {selectedticketRank} = this.state;

    return selectedticketRank;
  };

  onResetFilter = () => {
    const {clearOwnQuery} = this.props;
    clearOwnQuery && clearOwnQuery();
  };

  goBack = () => {
    const {
      showBYO,
      history: {goBack},
    } = this.props;

    /*
     * TODO: If / when event becomes a single page app with with discover, this will need to be updated
     * to listen for route changes in props instead of / in addition to the referrer.
     */
    const priorURLIsInternal = document.referrer.startsWith(`${window.location.protocol}//${window.location.host}`);
    if (!showBYO && !priorURLIsInternal) {
      this.goToPrimaryPerformerPage();
    } else {
      goBack();
    }
  };

  goToPrimaryPerformerPage = () => {
    const {performers = [], groupings = [], history} = this.props;
    let url = getEntityUrl(performers);
    if (!url) {
      url = getEntityUrl(groupings) || '';
    }
    history.push(`/${url}`);
  };

  checkVenueForDefaultPriceWithFees = (prevProps = {}) => {
    const {globalRegistry} = this.context;
    const {priceWithFees, venue = {}, updateQuery} = this.props;
    const defaultWebTLD = globalRegistry && globalRegistry.getDefaultWebTLD();
    const {venue: prevVenue = {}} = prevProps;
    if (venue.state === prevVenue.state) {
      return;
    }

    const stateMap = {Quebec: 'QC'};

    if (
      venue.state === stateMap.Quebec ||
      (venue.country.toLowerCase() === 'ca' && defaultWebTLD && defaultWebTLD.toLowerCase() === 'ca')
    ) {
      !priceWithFees && updateQuery && updateQuery({priceWithFees: true});
    }
  };

  fetchEvent = (eventId) => {
    const {fetchEvent} = this.props;
    if (__CLIENT__ && window && window.performance) {
      if (window.performance.clearMarks) {
        window.performance.clearMarks('mark-ticket-list-ready');
        window.performance.clearMarks('mark-event-init');
      }
      if (window.performance.mark) {
        window.performance.mark('mark-event-init');
      }
    }

    return fetchEvent && fetchEvent(eventId);
  };

  getLayoutConfig() {
    const {device} = this.props;
    // NOTE, there are many types like Parking, Hospitality that should be rewritten as Main for now
    const eventType = 'Main';
    const deviceType = device.type;

    return (deviceType && eventType && LayoutConfig[deviceType]?.[eventType]) || LayoutConfig.default;
  }

  render() {
    const {
      visitLocale,
      eventId,
      device,
      buyerMessages,
      isEventExpired = false,
      currencyCode,
      isRedirectionModalEnabled,
      showRedirectionModal,
      webURI,
    } = this.props;
    const {paypalError, paypalClientId, btToken} = this.state;
    const Layout = device.type === 'phone' ? MobileLayout : DesktopLayout;
    const layoutConfig = this.getLayoutConfig();
    const {globalRegistry} = this.context;

    const partner = ''; // Used to have the logic to check whether it was NFL. keeping it in case we want to add partners back

    const defaultWebTLD = this.context && globalRegistry && globalRegistry.getDefaultWebTLD();

    if (isEventExpired) {
      return (
        <section className="EventRoyal__ExpiredEvent">
          <ExpiredEvent goToPrimaryPerformerPage={this.goToPrimaryPerformerPage} />
        </section>
      );
    }

    return (
      <>
        <HeaderHacks eventId={eventId} />
        {isRedirectionModalEnabled && (
          <CBTRedirectionModal
            isOpen={showRedirectionModal}
            mode={CBT_MODAL_MODES.MANDATORY}
            location="event"
            uri={webURI}
            keepSearch
          />
        )}
        <TicketListProvider eventId={eventId} enableSSR={device.isBot || process.env.TICKET_LIST_MODULE_SSR_ENABLE} />
        <PaypalProvider
          currencyCode={currencyCode}
          locale={visitLocale}
          paypalClientId={paypalClientId}
          btToken={btToken}
          components="messages"
        >
          <Layout
            {...this.props}
            selectedTicketRank={this.getselectedticketRank()}
            onResetFilter={this.onResetFilter}
            handleSelectTicket={this.handleSelectTicket}
            onCloseBYO={this.onCloseBYO}
            goBack={this.goBack}
            partner={partner}
            buyerMessages={buyerMessages}
            offsetHeight={getHeaderHeightToOffsetApp()}
            isEventExpired={isEventExpired}
            defaultWebTLD={defaultWebTLD}
            layoutConfig={layoutConfig}
            eventCurrency={currencyCode}
            paypalError={paypalError}
            paypalClientId={paypalClientId}
            btToken={btToken}
          />
        </PaypalProvider>
      </>
    );
  }
}

EventRoyal.propTypes = {
  /** Important messages for the event */
  buyerMessages: PropTypes.array,

  /** Function to reset query values */
  clearOwnQuery: PropTypes.func,

  /** Event currency code */
  currencyCode: PropTypes.string,

  /** Device object with type info */
  device: PropTypes.object.isRequired,

  /** Event attributes */
  eventAttributes: PropTypes.object,

  /** The event venue country */
  eventCountry: PropTypes.string,

  /** The event id */
  eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /** The event type */
  eventType: PropTypes.string,

  /** Function to get event info */
  fetchEvent: PropTypes.func,

  /** Has filter changed? */
  filterChanged: PropTypes.bool,

  /** Function invoked to check and act when filter changes */
  filterChangeDetection: PropTypes.func,

  /** Event groupings */
  groupings: PropTypes.array,

  /** The router history object */
  history: PropTypes.object,

  /** Has the event expired? */
  isEventExpired: PropTypes.bool,

  /** Is paypal pay later enabled? */
  isPaypalPayLaterEnabled: PropTypes.bool,

  /** If price is shown with fees by default */
  isPriceIncludeFeesPreselected: PropTypes.bool,

  /** Function to check if query has changed */
  isQueryUpdated: PropTypes.func,

  /** Is loading? */
  loading: PropTypes.bool,

  /** Has event loaded? */
  loaded: PropTypes.bool,

  /** Event related performers */
  performers: PropTypes.array,

  /** Is price shown with fees? */
  priceWithFees: PropTypes.bool,

  /** Function called with default query values */
  setDefaultQueryValues: PropTypes.func.isRequired,

  /** Function invoked when ticket is selected or deselected */
  setListingSelected: PropTypes.func,

  /** Show BYO? */
  showBYO: PropTypes.bool,

  /** Current store id */
  storeId: PropTypes.number,

  /** The ticket quantity selected */
  quantity: PropTypes.number,

  /** Function called to update query */
  updateQuery: PropTypes.func,

  /** Event venue info */
  venue: PropTypes.object,

  /** User selected locale */
  visitLocale: PropTypes.string,

  /** Is redirection modal enabled? */
  isRedirectionModalEnabled: PropTypes.bool,

  /** Should redirection modal be shown? */
  showRedirectionModal: PropTypes.bool,

  /** The event webURI */
  webURI: PropTypes.string,

  /** Function to update default currencies */
  updateDefaultCurrencies: PropTypes.func,
};

export default withRouter(withQuery(EventRoyal));
